// src/components/Producto/ProductoList.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table,
  Input,
  Button,
  Badge,
  Label,
  Row,
  Col,
  Pagination,
  PaginationItem,
  PaginationLink,
  Card,
  CardBody,
  Alert,
} from "reactstrap";
import NewProductoModal from "./NewProductoModal";
import ConfirmProdcutoRemovalModal from "./ConfirmProdcutoRemovalModal";
import Moment from "moment";

// Custom hook for debouncing
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

// Custom hook for expanded rows
const useExpandedRows = () => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = useCallback((id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const isExpanded = useCallback((id) => expandedRows.has(id), [expandedRows]);

  return { toggleRow, isExpanded };
};

const ProductoList = ({ productos = [], resetState, itemsPerPage = 10 }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [tipoFilter, setTipoFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const { toggleRow, isExpanded } = useExpandedRows();
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Get unique tipos for filter
  const tipos = useMemo(() => {
    const set = new Set();
    (productos || []).forEach((p) => {
      if (p.tipo) set.add(p.tipo);
    });
    return Array.from(set).sort();
  }, [productos]);

  // Filter logic
  const filteredData = useMemo(() => {
    let rows = productos || [];

    // Tipo filter — `__blank__` sentinel filters to rows with a missing tipo
    // so admins can find and fix legacy Productos that predate the required
    // constraint on the form.
    if (tipoFilter === '__blank__') {
      rows = rows.filter((p) => !p.tipo || String(p.tipo).trim() === '');
    } else if (tipoFilter) {
      rows = rows.filter((p) => p.tipo === tipoFilter);
    }

    // Search filter
    if (debouncedSearchQuery.trim()) {
      const term = debouncedSearchQuery.toLowerCase();
      rows = rows.filter((p) => {
        return (
          (p.producto_code && p.producto_code.toLowerCase().includes(term)) ||
          (p.tipo && p.tipo.toLowerCase().includes(term)) ||
          (p.nombre && p.nombre.toLowerCase().includes(term))
        );
      });
    }

    return rows;
  }, [productos, debouncedSearchQuery, tipoFilter]);

  // Pagination
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredData.length / itemsPerPage)),
    [filteredData.length, itemsPerPage]
  );

  const safePage = useMemo(
    () => Math.min(Math.max(currentPage, 1), totalPages),
    [currentPage, totalPages]
  );

  const currentData = useMemo(() => {
    const indexOfLastItem = safePage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return filteredData.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredData, safePage, itemsPerPage]);

  // Event handlers
  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleTipoFilterChange = useCallback((e) => {
    setTipoFilter(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchQuery("");
    setTipoFilter("");
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Render expandable cell
  const renderCell = useCallback(
    (productId, text, style = {}) => {
      const expanded = isExpanded(productId);
      const displayText = text || "—";

      return (
        <td style={style} title={displayText}>
          <div
            className={`${expanded ? "" : "text-truncate"}`}
            role="button"
            tabIndex={0}
            aria-expanded={expanded}
            aria-label={
              expanded ? "Click to collapse" : "Click to expand full text"
            }
            onClick={() => toggleRow(productId)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleRow(productId);
              }
            }}
            style={{
              cursor: "pointer",
              maxWidth: "250px",
              wordBreak: "break-word",
            }}
          >
            {displayText}
          </div>
        </td>
      );
    },
    [isExpanded, toggleRow]
  );

  // Pagination controls
  const PaginationControls = useMemo(() => {
    if (totalPages <= 1) return null;

    const maxButtons = 5;
    let startPage = Math.max(1, safePage - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);

    if (endPage - startPage < maxButtons - 1) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    const pageButtons = [];
    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <PaginationItem key={i} active={safePage === i}>
          <PaginationLink
            onClick={() => handlePageChange(i)}
            aria-label={`Go to page ${i}`}
            aria-current={safePage === i ? "page" : undefined}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return (
      <Pagination className="mt-3 mb-0" aria-label="Product list pagination">
        <PaginationItem disabled={safePage === 1}>
          <PaginationLink
            previous
            onClick={() => handlePageChange(safePage - 1)}
            aria-label="Go to previous page"
          />
        </PaginationItem>

        {startPage > 1 && (
          <>
            <PaginationItem>
              <PaginationLink onClick={() => handlePageChange(1)}>
                1
              </PaginationLink>
            </PaginationItem>
            {startPage > 2 && (
              <PaginationItem disabled>
                <PaginationLink>...</PaginationLink>
              </PaginationItem>
            )}
          </>
        )}

        {pageButtons}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && (
              <PaginationItem disabled>
                <PaginationLink>...</PaginationLink>
              </PaginationItem>
            )}
            <PaginationItem>
              <PaginationLink onClick={() => handlePageChange(totalPages)}>
                {totalPages}
              </PaginationLink>
            </PaginationItem>
          </>
        )}

        <PaginationItem disabled={safePage === totalPages}>
          <PaginationLink
            next
            onClick={() => handlePageChange(safePage + 1)}
            aria-label="Go to next page"
          />
        </PaginationItem>
      </Pagination>
    );
  }, [totalPages, safePage, handlePageChange]);

  // Count legacy rows with no Tipo assigned — Tipo is now required at
  // create/edit time, but rows created before that constraint may be blank.
  const missingTipoCount = useMemo(
    () => (productos || []).filter((p) => !p.tipo || String(p.tipo).trim() === '').length,
    [productos]
  );

  return (
    <div>
      {missingTipoCount > 0 && tipoFilter !== '__blank__' && (
        <Alert color="warning" className="d-flex justify-content-between align-items-center mb-3">
          <span>
            <strong>{missingTipoCount}</strong>{' '}
            {missingTipoCount === 1 ? 'producto no tiene' : 'productos no tienen'} un Tipo asignado.
          </span>
          <Button color="secondary" size="sm" onClick={() => setTipoFilter('__blank__')}>
            Ver y arreglar
          </Button>
        </Alert>
      )}
      {/* Filter Card */}
      <Card className="mb-3 shadow-sm">
        <CardBody>
          <Row className="g-3">
            <Col md={4}>
              <Label htmlFor="tipo-filter" className="fw-bold small">
                📦 Tipo de Producto
              </Label>
              <Input
                type="select"
                id="tipo-filter"
                value={tipoFilter}
                onChange={handleTipoFilterChange}
                aria-label="Filter by tipo"
                style={{ fontSize: "0.95rem" }}
              >
                <option value="">Todos los tipos</option>
                <option value="__blank__">— Sin tipo asignado —</option>
                {tipos.map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {tipo}
                  </option>
                ))}
              </Input>
            </Col>

            <Col md={6}>
              <Label htmlFor="search-input" className="fw-bold small">
                🔍 Buscar
              </Label>
              <Input
                type="text"
                id="search-input"
                placeholder="Buscar por código, tipo o nombre..."
                value={searchQuery}
                onChange={handleSearchChange}
                aria-label="Search products"
                style={{ fontSize: "0.95rem" }}
              />
              {debouncedSearchQuery !== searchQuery && (
                <small className="text-muted">⏳ Buscando...</small>
              )}
            </Col>

            <Col md={2} className="d-flex align-items-end">
              <Button
                color="light"
                onClick={handleClearFilters}
                className="w-100"
                outline
              >
                🔄 Limpiar
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Results summary */}
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <div>
          <Badge color="primary" pill className="me-2">
            {currentData.length}
          </Badge>
          <span className="text-muted small">
            de <strong>{filteredData.length}</strong> producto(s)
            {filteredData.length !== productos.length && (
              <span className="ms-1">
                (filtrado de <strong>{productos.length}</strong> total)
              </span>
            )}
          </span>
        </div>
        <div className="text-muted small">
          Página {safePage} de {totalPages}
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <Table
          hover
          className="align-middle mb-0"
          style={{ fontSize: "0.9rem" }}
        >
          <thead
            style={{
              backgroundColor: "#f8f9fa",
              position: "sticky",
              top: 0,
            }}
          >
            <tr>
              <th style={{ minWidth: "120px" }}>🏷️ Código</th>
              <th style={{ minWidth: "150px" }}>📦 Tipo</th>
              <th style={{ minWidth: "250px" }}>📋 Producto</th>
              <th style={{ minWidth: "150px" }}>📅 Fecha Registro</th>
              <th style={{ minWidth: "140px", textAlign: "center" }}>
                ⚙️ Acciones
              </th>
            </tr>
          </thead>

          <tbody>
            {currentData.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-5">
                  {productos.length === 0 ? (
                    <div>
                      <div style={{ fontSize: "3rem" }}>📦</div>
                      <h5 className="mt-3">No hay productos registrados</h5>
                      <p className="text-muted">
                        Comienza agregando tu primer producto
                      </p>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize: "3rem" }}>🔍</div>
                      <h5 className="mt-3">No se encontraron resultados</h5>
                      <p className="text-muted">
                        Intenta ajustar los filtros de búsqueda
                      </p>
                      <Button
                        color="link"
                        onClick={handleClearFilters}
                        className="mt-2"
                      >
                        Limpiar filtros
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              currentData.map((producto, idx) => {
                const productId = producto.pk;

                return (
                  <tr
                    key={productId}
                    style={{
                      backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f8f9fa",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#e9ecef")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        idx % 2 === 0 ? "#ffffff" : "#f8f9fa")
                    }
                  >
                    <td>
                      <Badge color="info" className="font-monospace">
                        {producto.producto_code?.toUpperCase() || "—"}
                      </Badge>
                    </td>
                    {renderCell(productId, producto.tipo)}
                    {renderCell(productId, producto.nombre)}
                    <td>
                      {producto.registration_date ? (
                        <span className="small">
                          {Moment(producto.registration_date).format(
                            "DD/MM/YYYY HH:mm"
                          )}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-center">
                      <div className="d-flex gap-1 justify-content-center">
                        <NewProductoModal
                          create={false}
                          producto={producto}
                          resetState={resetState}
                        />
                        <ConfirmProdcutoRemovalModal
                          pk={productId}
                          resetState={resetState}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      {PaginationControls && (
        <div className="d-flex justify-content-center mt-3">
          {PaginationControls}
        </div>
      )}
    </div>
  );
};

export default ProductoList;